import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickUploadWidget = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const navigate = useNavigate();

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    
    if (imageFiles?.length > 0) {
      navigate('/photo-upload', { state: { droppedFiles: imageFiles } });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      navigate('/photo-upload', { state: { selectedFiles: files } });
    }
  };

  const handleCameraCapture = () => {
    navigate('/photo-upload', { state: { openCamera: true } });
  };

  const handleFullUpload = () => {
    navigate('/photo-upload');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Quick Upload</h3>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver 
            ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50 hover:bg-accent/5'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="Upload" size={24} color="var(--color-accent)" />
        </div>
        
        <h4 className="font-medium text-foreground mb-2">
          {isDragOver ? 'Drop your bike photo here' : 'Upload bike photo'}
        </h4>
        
        <p className="text-sm text-muted-foreground mb-4">
          Drag & drop or click to select your bike image for instant analysis
        </p>

        <div className="space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="quick-upload-input"
            multiple
          />
          
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="Upload"
            iconPosition="left"
            onClick={() => document.getElementById('quick-upload-input')?.click()}
          >
            Select Photos
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Camera"
            iconPosition="left"
            onClick={handleCameraCapture}
          >
            Take Photo
          </Button>
        </div>
      </div>
      <div className="mt-6 space-y-3">
        <h4 className="font-medium text-foreground">Quick Actions</h4>
        
        <div className="space-y-2">
          <button
            onClick={() => navigate('/wishlist')}
            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-colors interactive-scale"
          >
            <div className="w-8 h-8 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="Heart" size={16} color="var(--color-error)" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">My Wishlist</p>
              <p className="text-xs text-muted-foreground">Saved recommendations</p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
          </button>

          <button
            onClick={() => navigate('/local-shop-finder')}
            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-colors interactive-scale"
          >
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="MapPin" size={16} color="var(--color-accent)" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Find Shops</p>
              <p className="text-xs text-muted-foreground">Local bike stores</p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
          </button>

          <button
            onClick={() => navigate('/upgrade-recommendations')}
            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-colors interactive-scale"
          >
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={16} color="var(--color-warning)" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Browse All</p>
              <p className="text-xs text-muted-foreground">All recommendations</p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <Button
          variant="outline"
          fullWidth
          iconName="Upload"
          iconPosition="left"
          onClick={handleFullUpload}
        >
          Full Upload Experience
        </Button>
      </div>
    </div>
  );
};

export default QuickUploadWidget;